import React, { useContext, useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link, useHistory, useParams } from "react-router-dom";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import {
  CreateOrUpdateResponseUser_Status,
  CreateOrUpdateUserOrigin_Source,
  CreateOrUpdateUsersJob,
} from "@safetyculture/s12-apis-connect-web/s12/users/v1/create_or_update_pb";

import { UrlParamTypes } from "../types/org-display";
import { adminListCreateOrUpdateUsersJobs } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import {
  compareJobsByInitiatedAtDesc,
  countByUserStatus,
  formatDateOnly,
  formatOrigin,
  getPerformedBy,
  getResolvedPerformedBy,
  isIntegrationSource,
  isValidateOnlyJob,
} from "components/org-user-jobs/helpers";
import { JobStatusChip } from "components/org-user-jobs/job-status-chip";
import { fetchUserNameMap } from "components/org-user-jobs/user-lookup";
import { DocPrefix } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { toS12ID, toUUID } from "helpers/s12-helpers";

type OriginFilter = "csv" | "api" | "integration";

const matchesOriginFilter = (
  job: CreateOrUpdateUsersJob,
  filters: Set<OriginFilter>
): boolean => {
  if (filters.size === 0) {
    return true;
  }
  const source =
    job.origin?.source ?? CreateOrUpdateUserOrigin_Source.UNSPECIFIED;
  if (filters.has("csv") && source === CreateOrUpdateUserOrigin_Source.CSV) {
    return true;
  }
  if (
    filters.has("api") &&
    source === CreateOrUpdateUserOrigin_Source.PUBLIC_API
  ) {
    return true;
  }
  if (filters.has("integration") && isIntegrationSource(source)) {
    return true;
  }
  return false;
};

const FILTER_OPTIONS: Array<{ key: OriginFilter; label: string }> = [
  { key: "csv", label: "CSV" },
  { key: "api", label: "API" },
  { key: "integration", label: "Integration" },
];

const OrgUserJobs = () => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const history = useHistory();
  const { showError } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const canListJobs = hasPermissions([
    ActionType.ADMIN_LIST_CREATE_OR_UPDATE_USERS_JOBS,
  ]);

  const orgUUID = toUUID(roleId);

  const [jobs, setJobs] = useState<CreateOrUpdateUsersJob[]>([]);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const [filters, setFilters] = useState<Set<OriginFilter>>(new Set());
  const [draftFilters, setDraftFilters] = useState<Set<OriginFilter>>(
    new Set()
  );
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [showValidateOnly, setShowValidateOnly] = useState(false);

  useEffect(() => {
    if (permissions.isPermissionsLoading || !canListJobs) {
      return;
    }
    setIsLoading(true);
    adminListCreateOrUpdateUsersJobs(
      { orgId: orgUUID },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        setJobs(res.jobs);
        const creatorS12Ids = res.jobs
          .filter(
            (j) =>
              j.creatorId &&
              !isIntegrationSource(
                j.origin?.source ?? CreateOrUpdateUserOrigin_Source.UNSPECIFIED
              )
          )
          .map((j) => toS12ID(DocPrefix.PrefixUser, j.creatorId));
        if (creatorS12Ids.length > 0) {
          setIsLoadingNames(true);
          fetchUserNameMap(creatorS12Ids, roleId, regionCode, roleId)
            .then(setUserNames)
            .catch(showError)
            .finally(() => setIsLoadingNames(false));
        }
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionCode, roleId, permissions.isPermissionsLoading, canListJobs]);

  const filteredJobs = useMemo(
    () =>
      jobs
        .filter((j) => showValidateOnly || !isValidateOnlyJob(j))
        .filter((j) => matchesOriginFilter(j, filters))
        .slice()
        .sort(compareJobsByInitiatedAtDesc),
    [jobs, filters, showValidateOnly]
  );

  if (permissions.isPermissionsLoading) {
    return (
      <CardContent>
        <CircularProgress data-testid="import-history-perm-loading" />
      </CardContent>
    );
  }

  if (!canListJobs) {
    return (
      <CardContent>
        <Typography data-testid="import-history-no-permission">
          You do not have permission to view import history
        </Typography>
      </CardContent>
    );
  }

  const openFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraftFilters(new Set(filters));
    setFilterAnchor(event.currentTarget);
  };

  const closeFilter = () => setFilterAnchor(null);

  const toggleDraftFilter = (key: OriginFilter) => {
    setDraftFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleClearAll = () => {
    setDraftFilters(new Set());
  };

  const handleApplyFilters = () => {
    setFilters(new Set(draftFilters));
    closeFilter();
  };

  const handleResetFilters = () => {
    setFilters(new Set());
    setDraftFilters(new Set());
  };

  const filterButtonLabel =
    filters.size === 0
      ? "Filter by Origin"
      : `Origin: ${Array.from(filters)
          .map((k) => FILTER_OPTIONS.find((f) => f.key === k)?.label)
          .filter(Boolean)
          .join(", ")}`;

  const goToJob = (job: CreateOrUpdateUsersJob) => {
    const resolved = getResolvedPerformedBy(job, userNames);
    history.push({
      pathname: `/organisation/${regionCode}/${roleId}/import-history/${job.id}`,
      state: resolved ? { performedByName: resolved } : undefined,
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to={`/organisation/${regionCode}/${roleId}`}
            className="remove-link-style"
            style={{ color: "inherit" }}
          >
            Users
          </Link>
          <Typography color="textPrimary" data-testid="import-history-title">
            <b>Import history</b>
          </Typography>
        </Breadcrumbs>
      </Grid>

      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          sx={{ width: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="outlined"
              color="primary"
              onClick={openFilter}
              data-testid="import-history-filter-button"
            >
              {filterButtonLabel}
            </Button>
            {filters.size > 0 && (
              <Button
                size="small"
                onClick={handleResetFilters}
                aria-label="clear filters"
                data-testid="import-history-clear-filters"
                sx={{ minWidth: 0, padding: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </Button>
            )}
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={showValidateOnly}
                onChange={(_, checked) => setShowValidateOnly(checked)}
                data-testid="import-history-show-validate-only"
              />
            }
            label="Show validate only jobs"
          />
        </Stack>
        <Popover
          open={Boolean(filterAnchor)}
          anchorEl={filterAnchor}
          onClose={closeFilter}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Stack>
              {FILTER_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  control={
                    <Checkbox
                      checked={draftFilters.has(opt.key)}
                      onChange={() => toggleDraftFilter(opt.key)}
                      data-testid={`import-history-filter-checkbox-${opt.key}`}
                    />
                  }
                  label={opt.label}
                />
              ))}
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2 }}
            >
              <Button size="small" onClick={handleClearAll}>
                Clear all
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyFilters}
                data-testid="import-history-filter-done"
              >
                Done
              </Button>
            </Stack>
          </Box>
        </Popover>
      </Grid>

      <Grid item xs={12}>
        {!isLoading && filteredJobs.length === 0 ? (
          <Alert
            severity="info"
            variant="standard"
            data-testid="import-history-empty"
          >
            {jobs.length === 0
              ? "No import history found for this organisation."
              : "No jobs match the selected filters."}
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table
              aria-label="import-history"
              sx={{
                tableLayout: "fixed",
                minWidth: 720,
                "& thead th, & thead th .MuiTypography-root": {
                  fontWeight: 700,
                },
              }}
            >
              <colgroup>
                <col style={{ width: "13%" }} />
                <col style={{ width: "19%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "8%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>Uploaded on</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Performed by</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>Total users</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>Users with errors</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>User uploads failed</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Origin</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Status</Typography>
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody data-testid="import-history-tbody">
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredJobs.map((job) => {
                    const source =
                      job.origin?.source ??
                      CreateOrUpdateUserOrigin_Source.UNSPECIFIED;
                    const creatorS12 = job.creatorId
                      ? toS12ID(DocPrefix.PrefixUser, job.creatorId)
                      : "";
                    const performedByUnresolved =
                      isLoadingNames &&
                      !!creatorS12 &&
                      !isIntegrationSource(source) &&
                      !userNames.has(creatorS12);
                    return (
                      <TableRow
                        key={job.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => goToJob(job)}
                        data-testid={`job-row-${job.id}`}
                      >
                        <TableCell>{formatDateOnly(job.initiatedAt)}</TableCell>
                        <TableCell sx={{ wordBreak: "break-all" }}>
                          {performedByUnresolved ? (
                            <Skeleton
                              width={140}
                              data-testid={`performed-by-loading-${job.id}`}
                            />
                          ) : (
                            getPerformedBy(job, userNames)
                          )}
                        </TableCell>
                        <TableCell align="right">{job.totalUsers}</TableCell>
                        <TableCell align="right">
                          {countByUserStatus(job, [
                            CreateOrUpdateResponseUser_Status.COMPLETED_WITH_ERRORS,
                            CreateOrUpdateResponseUser_Status.VALIDATED_WITH_ERRORS,
                          ])}
                        </TableCell>
                        <TableCell align="right">
                          {countByUserStatus(job, [
                            CreateOrUpdateResponseUser_Status.ERRORED,
                          ])}
                        </TableCell>
                        <TableCell>{formatOrigin(job.origin)}</TableCell>
                        <TableCell>
                          <JobStatusChip status={job.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToJob(job);
                            }}
                            data-testid={`view-job-${job.id}`}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  );
};

export default OrgUserJobs;
